import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, SHOW_PRICING } from '../App';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, Sparkles, Image, Instagram, FileText, Camera, 
  Map, Star, CreditCard, Trophy, Users, Calendar, Settings, LogOut,
  ChevronLeft, Menu, X, Palette, Crown, Wand2, MessageSquare, Gift, ImagePlus,
  CalendarDays, Target, Globe, Briefcase, Handshake, Award, Bot, BookOpen, Compass, FileCheck, Heart, TrendingUp, Gem, Rocket
} from 'lucide-react';
import { useState } from 'react';
import AIAssistantWidget from './AIAssistantWidget';
import LanguageSelector from './LanguageSelector';
import { NotificationBell, NotificationCenter, useNotificationCount } from './NotificationCenter';

const navItems = [
  { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/dashboard' },
  { icon: Rocket, labelKey: 'nav.visionPlan', path: '/vision-plan', highlight: true, special: true },
  { icon: TrendingUp, labelKey: 'nav.evolution', path: '/journey-evolution', highlight: true },
  { icon: Gem, labelKey: 'nav.myRevelations', path: '/my-revelations', highlight: true, special: true },
  { icon: Heart, labelKey: 'nav.visionSetup', path: '/vision-setup', highlight: true, special: true },
  { icon: Image, labelKey: 'nav.visionBoard', path: '/vision-board-generator', highlight: true },
  { icon: Bot, labelKey: 'nav.aiAssistant', path: '/ai-assistant' },
  { icon: Award, labelKey: 'nav.brandJourney', path: '/brand-journey' },
  { icon: BookOpen, labelKey: 'nav.monthlyJournal', path: '/monthly-journal' },
  { icon: Compass, labelKey: 'nav.quarterlyReview', path: '/quarterly-review' },
  { icon: FileCheck, labelKey: 'nav.annualReport', path: '/annual-report' },
  { icon: Palette, labelKey: 'nav.brandAudit', path: '/brand-audit' },
  { icon: Sparkles, labelKey: 'nav.visionIdentity', path: '/vision-identity' },
  { icon: Wand2, labelKey: 'nav.brandGlowUp', path: '/brand-glow-up' },
  { icon: CreditCard, labelKey: 'nav.brandIdentityCard', path: '/brand-identity-card' },
  { icon: ImagePlus, labelKey: 'nav.visionBoardStudio', path: '/vision-board', tier: 'premium' },
  { icon: Instagram, labelKey: 'nav.instagramStrategy', path: '/instagram-strategy', tier: 'premium' },
  { icon: Target, labelKey: 'nav.contentStrategy', path: '/content-strategy' },
  { icon: CalendarDays, labelKey: 'nav.contentPlanning', path: '/content-planning-studio', tier: 'premium' },
  { icon: FileText, labelKey: 'nav.contentGenerator', path: '/content-generator' },
  { icon: Camera, labelKey: 'nav.photoshootConcept', path: '/photoshoot-concept', tier: 'premium' },
  { icon: Map, labelKey: 'nav.visualRoadmap', path: '/visual-roadmap' },
  { icon: Star, labelKey: 'nav.futureBrand', path: '/future-brand', tier: 'pro' },
  { icon: Trophy, labelKey: 'nav.challenges', path: '/challenges' },
  { icon: Globe, labelKey: 'nav.womenConnect', path: '/women-connect' },
  { icon: Briefcase, labelKey: 'nav.businessDirectory', path: '/business-directory', tier: 'pro' },
  { icon: Users, labelKey: 'nav.community', path: '/community' },
  { icon: Heart, labelKey: 'nav.experiences', path: '/experiences' },
  { icon: MessageSquare, labelKey: 'nav.reviews', path: '/reviews' },
  { icon: Gift, labelKey: 'nav.inviteShine', path: '/invite' },
  { icon: Calendar, labelKey: 'nav.bookCoaching', path: '/coaching' },
];

export const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getTierBadge = (tier) => {
    if (tier === 'premium') return (
      <span className="text-[9px] font-accent tracking-wider text-[#E5C585] ml-auto px-2 py-0.5 bg-[#E5C585]/10 border border-[#E5C585]/20">
        PREMIUM
      </span>
    );
    if (tier === 'pro') return (
      <span className="text-[9px] font-accent tracking-wider text-[#D4AF37] ml-auto px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20">
        PRO
      </span>
    );
    return null;
  };

  // Notification state
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const notificationCount = useNotificationCount();

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        onTouchEnd={(e) => {
          // iOS Safari fix: Ensure touch event triggers state change
          e.preventDefault();
          setSidebarOpen(!sidebarOpen);
        }}
        className="lg:hidden fixed top-5 left-5 z-50 p-2.5 bg-[#0A0A0A] border border-[#1C1C1C] text-white hover:border-[#D4AF37]/30 transition-colors"
        data-testid="mobile-menu-btn"
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        aria-label={sidebarOpen ? "Fermer le menu" : "Ouvrir le menu"}
      >
        {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-[#050505] border-r border-[#1C1C1C] transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#1C1C1C]">
            <Link to="/dashboard" className="group block" data-testid="sidebar-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_brand-alchemy-2/artifacts/3hu8wfzt_24FA2A63-8890-4D16-8044-60FA948D15B0.png" 
                alt="Clierxter Vision Lab" 
                className="h-16 w-auto object-contain"
              />
            </Link>
          </div>

          {/* User info */}
          <div className="p-6 border-b border-[#1C1C1C]">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full border border-[#D4AF37]/20" />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-[#1C1C1C] to-[#0A0A0A] rounded-full flex items-center justify-center border border-[#D4AF37]/20">
                  <span className="text-[#E5C585] font-serif text-lg">{user?.name?.charAt(0)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Crown className="text-[#D4AF37]" size={12} />
                  <p className="text-[#D4AF37] text-xs font-accent tracking-wider uppercase">{user?.subscription_tier || 'Free'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6">
            <ul className="space-y-1 px-4">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 text-sm transition-all relative group ${
                        isActive 
                          ? 'text-[#E5C585] bg-[#E5C585]/5' 
                          : item.special
                            ? 'text-white bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 hover:border-[#D4AF37]/50'
                            : item.highlight
                              ? 'text-[#D4AF37] hover:text-[#E5C585] hover:bg-[#D4AF37]/5 border border-[#D4AF37]/20'
                              : 'text-[#71717A] hover:text-white hover:bg-white/[0.02]'
                      }`}
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-gradient-to-b from-[#E5C585] to-[#D4AF37]" />
                      )}
                      <Icon size={18} strokeWidth={1.5} className={item.special ? 'text-[#D4AF37]' : ''} />
                      <span className="font-light">{t(item.labelKey)}</span>
                      {item.special && !isActive && (
                        <span className="ml-auto text-[8px] px-1.5 py-0.5 bg-[#D4AF37] text-black font-accent">AI</span>
                      )}
                      {item.highlight && !item.special && !isActive && (
                        <span className="ml-auto text-[8px] px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] font-accent">NEW</span>
                      )}
                      {getTierBadge(item.tier)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Bottom actions */}
          <div className="p-4 border-t border-[#1C1C1C] space-y-2">
            {SHOW_PRICING && (
              <Link
                to="/pricing"
                className="flex items-center justify-center gap-2 px-4 py-3 btn-gold w-full"
                data-testid="upgrade-btn"
              >
                <Sparkles size={16} />
                {t('subscription.upgrade')}
              </Link>
            )}
            <Link
              to="/settings"
              className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                location.pathname === '/settings' 
                  ? 'text-[#E5C585]' 
                  : 'text-[#52525B] hover:text-white'
              }`}
              data-testid="nav-settings"
            >
              <Settings size={18} strokeWidth={1.5} />
              <span className="font-light">{t('nav.settings')}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-sm text-[#52525B] hover:text-red-400 w-full transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} strokeWidth={1.5} />
              <span className="font-light">{t('auth.signOut')}</span>
            </button>
            
            {/* Admin links - only for admin accounts */}
            {user?.email && ['manuemsla@gmail.com', 'felixmanuela.manda@gmail.com', 'manuemslaclierxter@gmail.com'].includes(user.email.toLowerCase()) && (
              <div className="pt-2 mt-2 border-t border-[#1C1C1C]/50">
                <p className="text-[8px] text-[#52525B] px-4 mb-2 uppercase tracking-wider">Admin</p>
                <Link
                  to="/admin/feedback"
                  className="flex items-center gap-3 px-4 py-2 text-xs text-[#52525B] hover:text-[#D4AF37] transition-colors"
                  data-testid="admin-feedback-link"
                >
                  <MessageSquare size={14} strokeWidth={1.5} />
                  <span>Gestion Feedbacks</span>
                </Link>
                <Link
                  to="/admin/scheduled-posts"
                  className="flex items-center gap-3 px-4 py-2 text-xs text-[#52525B] hover:text-[#D4AF37] transition-colors"
                  data-testid="admin-posts-link"
                >
                  <Calendar size={14} strokeWidth={1.5} />
                  <span>Publications Planifiées</span>
                </Link>
              </div>
            )}
            
            {/* Feedback link - for all users */}
            <div className="pt-4 mt-2 border-t border-[#1C1C1C]/30">
              <a
                href="mailto:feedback@clierxter.com?subject=Feedback%20Vision%20Lab"
                className="flex items-center gap-3 px-4 py-2 text-xs text-[#52525B] hover:text-[#E5C585] transition-colors"
                data-testid="share-feedback-btn"
              >
                <Heart size={14} strokeWidth={1.5} />
                <span>Partager ton avis</span>
              </a>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen lg:ml-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#050505]/90 backdrop-blur-xl border-b border-[#1C1C1C] px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {title && (
                <>
                  <button
                    onClick={() => navigate(-1)}
                    className="text-[#52525B] hover:text-[#E5C585] transition-colors"
                    data-testid="back-btn"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <div>
                    <h1 className="font-serif text-2xl md:text-3xl text-white">{title}</h1>
                    <div className="w-12 h-px bg-gradient-to-r from-[#D4AF37] to-transparent mt-2" />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 relative">
              {/* Notification Bell */}
              <NotificationBell 
                count={notificationCount} 
                onClick={() => setNotificationCenterOpen(!notificationCenterOpen)}
              />
              <NotificationCenter 
                isOpen={notificationCenterOpen} 
                onClose={() => setNotificationCenterOpen(false)}
              />
              
              <LanguageSelector variant="header" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-8 lg:p-12">
          {children}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* AI Assistant Widget */}
      <AIAssistantWidget />
    </div>
  );
};

export default DashboardLayout;
